import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { fmtErr } from "../lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Ticket, BookOpen, Users, Settings, LogOut, LifeBuoy, Menu, Undo2, ShieldAlert, Boxes, MonitorSmartphone, BarChart3, Undo, Contact } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const NAV = [
  { to: "/desk", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["hd_admin", "hd_agent", "hd_user"] },
  { to: "/desk/tickets", label: "Tickets", icon: Ticket, roles: ["hd_admin", "hd_agent", "hd_user"] },
  { to: "/desk/crm", label: "CRM", icon: Contact, roles: ["hd_admin", "hd_agent"] },
  { to: "/desk/kb", label: "Knowledge Base", icon: BookOpen, roles: ["hd_admin", "hd_agent", "hd_user"] },
  { to: "/desk/siem", label: "SIEM", icon: ShieldAlert, roles: ["hd_admin", "hd_agent"] },
  { to: "/desk/assets", label: "Assets", icon: Boxes, roles: ["hd_admin", "hd_agent", "hd_user"] },
  { to: "/desk/mdm", label: "Devices (MDM)", icon: MonitorSmartphone, roles: ["hd_admin", "hd_agent"] },
  { to: "/desk/analytics", label: "Analytics", icon: BarChart3, roles: ["hd_admin"] },
  { to: "/desk/users", label: "Users", icon: Users, roles: ["hd_admin"] },
  { to: "/desk/settings", label: "Settings", icon: Settings, roles: ["hd_admin"] },
];

const lighten = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  const c = (v) => Math.min(255, Math.round(((n >> v) & 255) * 1.22) + 10);
  return `#${((c(16) << 16) | (c(8) << 8) | c(0)).toString(16).padStart(6, "0")}`;
};

export const DeskLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const stopImpersonation = async () => {
    try {
      await api.post("/admin/stop-impersonation");
      window.location.assign("/admin/helpdesk");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const [avail, setAvail] = useState(null);
  useEffect(() => {
    const h = (e) => setAvail(e.detail);
    window.addEventListener("hd-avail", h);
    return () => window.removeEventListener("hd-avail", h);
  }, []);
  const toggleAvail = async () => {
    const next = !(avail ?? user?.hd_available !== false);
    try {
      await api.put("/helpdesk/availability", { available: next });
      setAvail(next);
      window.dispatchEvent(new CustomEvent("hd-avail", { detail: next }));
      toast.success(next ? "You are now available for new assignments" : "You are now away — no auto-assignments");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const [company, setCompany] = useState(null);
  const [blocked, setBlocked] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/helpdesk/company").then((r) => setCompany(r.data)).catch((e) => {
      if (e?.response?.status === 403) setBlocked(e.response.data?.detail || "Access blocked");
    });
  }, []);
  const links = NAV.filter((n) => n.roles.includes(user?.role === "super_admin" ? "hd_admin" : user?.role));
  const sub = company?.subscription;
  const brand = /^#[0-9a-fA-F]{6}$/.test(company?.brand_color || "") ? company.brand_color : null;
  const brandVars = brand ? { "--brand-primary": brand, "--brand-primary-hover": lighten(brand) } : {};

  if (blocked) {
    return (
      <div className="min-h-screen bg-[#F1F4F8] flex items-center justify-center px-4" data-testid="desk-blocked-screen">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <LifeBuoy size={32} className="mx-auto text-red-500" />
          <h1 className="text-xl font-bold font-heading mt-4">Workspace unavailable</h1>
          <p className="text-sm text-slate-500 mt-2" data-testid="desk-blocked-message">{blocked}</p>
          <button data-testid="desk-blocked-logout" onClick={async () => { await logout(); navigate("/login"); }}
            className="mt-6 px-6 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Log out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#F1F4F8]" data-testid="desk-layout" style={brandVars}>
      <aside className={`${open ? "flex" : "hidden"} md:flex flex-col w-60 shrink-0 bg-white border-r border-slate-200 fixed md:sticky top-0 h-screen z-40`}>
        <Link to="/desk" className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-100 font-bold font-heading text-lg" data-testid="desk-logo">
          {company?.logo_url ? (
            <img src={`${API_BASE}${company.logo_url}`} alt={company?.name || "logo"} data-testid="desk-brand-logo"
              className="h-9 max-w-[160px] object-contain" />
          ) : (
            <>
              <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: "var(--brand-primary)" }}>
                <LifeBuoy size={17} />
              </span>
              <span style={{ color: "var(--brand-primary)" }}>GloriDesk</span>
            </>
          )}
        </Link>
        <p className="px-5 pt-4 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 truncate">{company?.name || "..."}</p>
        <nav className="flex-1 px-3 space-y-0.5">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
              data-testid={`desk-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "desk-nav-active" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`}>
              <l.icon size={17} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-100 text-xs">
          {sub && (
            <p data-testid="desk-sub-status" className={sub.active ? "text-emerald-600 font-medium" : "text-red-600 font-medium"}>
              {sub.status === "platform" ? "Platform workspace" :
                sub.status === "trial" ? `Trial — ${sub.days_left ?? 0} days left` :
                sub.active ? `Active — ${sub.days_left} days left` : "Subscription expired"}
            </p>
          )}
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        {user?.impersonated && (
          <div data-testid="desk-impersonation-banner" className="bg-amber-600 text-white text-sm font-medium px-4 py-2 flex items-center justify-center gap-3 sticky top-0 z-50">
            <span>Viewing as <b>{user.name}</b> ({user.email}) — impersonation mode</span>
            <button data-testid="desk-stop-impersonation" onClick={stopImpersonation}
              className="inline-flex items-center gap-1.5 bg-white/20 hover:bg-white/30 rounded-md px-3 py-1 text-xs font-semibold">
              <Undo2 size={13} /> Return to admin
            </button>
          </div>
        )}
        <header className="h-16 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30 text-white shadow-sm" style={{ background: "var(--brand-primary)" }}>
          <div className="flex items-center gap-3">
            <button className="md:hidden p-2 text-white/80" onClick={() => setOpen((o) => !o)} data-testid="desk-menu-toggle"><Menu size={20} /></button>
            <p className="text-sm text-white/70 truncate max-w-[45vw] md:max-w-none">{company?.name ? `${company.name} — support workspace` : "Helpdesk workspace"}</p>
          </div>
          <div className="flex items-center gap-3">
            {(user?.role === "hd_admin" || user?.role === "hd_agent" || user?.role === "super_admin") && (() => {
              const on = avail ?? user?.hd_available !== false;
              return (
                <button data-testid="availability-toggle" onClick={toggleAvail} title="Toggle your availability for auto-assignment"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    on ? "bg-emerald-400/15 text-emerald-300 border-emerald-300/30" : "bg-white/10 text-white/60 border-white/20"}`}>
                  <span className={`w-2 h-2 rounded-full ${on ? "bg-emerald-400" : "bg-white/40"}`} />
                  {on ? "Available" : "Away"}
                </button>
              );
            })()}
            <span className="text-sm text-white/85" data-testid="desk-user-name">{user?.name}
              <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-white/15 border border-white/20 px-1.5 py-0.5 rounded">
                {user?.role === "hd_admin" || user?.role === "super_admin" ? "Admin" : user?.role === "hd_agent" ? "Agent" : "User"}
              </span>
            </span>
            {user?.role === "super_admin" && (
              <a href="/admin" data-testid="desk-back-to-admin" title="Back to platform admin"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/10 border border-white/20 text-white/80 hover:bg-white/20">
                <Undo size={12} /> Platform admin
              </a>
            )}
            <button data-testid="desk-logout" onClick={async () => { await logout(); navigate("/login"); }}
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-md"><LogOut size={17} /></button>
          </div>
        </header>
        {sub && !sub.active && (
          <div className="bg-red-600 text-white text-sm px-4 py-2 text-center" data-testid="desk-expired-banner">
            Your subscription has expired — <Link to="/desk/settings" className="underline font-semibold">renew now</Link> to keep your helpdesk running.
          </div>
        )}
        <main className="p-4 sm:p-6 max-w-7xl">{children}</main>
      </div>
    </div>
  );
};

export const PRIORITY_STYLE = {
  P1: "bg-red-50 text-red-700 border-red-200", P2: "bg-orange-50 text-orange-700 border-orange-200",
  P3: "bg-amber-50 text-amber-700 border-amber-200", P4: "bg-neutral-50 text-neutral-600 border-neutral-200" };
export const STATUS_STYLE = {
  new: "bg-sky-50 text-sky-700 border-sky-200", in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  on_hold: "bg-amber-50 text-amber-700 border-amber-200", resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-neutral-100 text-neutral-500 border-neutral-200" };
export const STATUSES = ["new", "in_progress", "on_hold", "resolved", "closed"];
export const PRIORITIES = ["P1", "P2", "P3", "P4"];
export const CATEGORIES = ["Hardware", "Software", "Network", "Access", "Email", "Other"];
export const Badge = ({ value, map, testId }) => (
  <span data-testid={testId} className={`text-xs font-medium border rounded-full px-2.5 py-0.5 whitespace-nowrap ${map[value] || "bg-neutral-50 border-neutral-200"}`}>
    {String(value).replace(/_/g, " ")}
  </span>
);
